import {
  BULB,
  CROSS,
  EMPTY,
  MAX_SIZE,
  WHITE,
  type AkariDefinition,
  type AkariProgress,
  type AkariSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Place light bulbs in white cells. A bulb lights its whole row and column, up to the first black cell. Every white cell must be lit, and no bulb may light another. A number on a black cell tells how many bulbs sit right next to it, horizontally or vertically.",
  fr: "Placez des ampoules sur les cases blanches. Une ampoule éclaire toute sa ligne et toute sa colonne, jusqu'à la première case noire. Chaque case blanche doit être éclairée, et aucune ampoule ne doit en éclairer une autre. Un nombre sur une case noire indique combien d'ampoules la touchent, horizontalement ou verticalement.",
};

export function emptyDefinition(): AkariDefinition {
  return {
    puzzleType: "akari",
    typeVersion: 1,
    rows: 7,
    cols: 7,
    grid: blankRows(7, 7),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

export function blankRows(rows: number, cols: number): string[] {
  return Array.from({ length: rows }, () => EMPTY.repeat(cols));
}

/** Crops or pads a row set to the given size, keeping the top-left corner. */
export function resizeRows(source: string[], rows: number, cols: number): string[] {
  return Array.from({ length: rows }, (_, r) => {
    const row = source[r] ?? "";
    return row.length >= cols ? row.slice(0, cols) : row + EMPTY.repeat(cols - row.length);
  });
}

function sanitizeRow(raw: unknown, allowed: readonly string[]): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw, (char) => (allowed.includes(char) ? char : EMPTY)).join("");
}

/** Marks only sit on white cells: whatever a stored blob says on a black one is dropped. */
export function clearBlacks(marks: string[], grid: string[]): string[] {
  return marks.map((row, r) =>
    Array.from(row, (char, c) => (grid[r]?.[c] === WHITE ? char : EMPTY)).join(""),
  );
}

export function emptyProgress(definition: AkariDefinition): AkariProgress {
  return { version: 1, rows: blankRows(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: AkariDefinition, raw: unknown): AkariProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(source)) return emptyProgress(definition);
  const rows = source.map((row) => sanitizeRow(row, [BULB, CROSS, EMPTY]));
  return {
    version: 1,
    rows: clearBlacks(resizeRows(rows, definition.rows, definition.cols), definition.grid),
  };
}

/** The server only reads bulbs: crosses are the player's own notes. */
export function serializeSubmission(
  definition: AkariDefinition,
  progress: AkariProgress,
): AkariSubmission {
  return {
    rows: clearBlacks(
      resizeRows(progress.rows, definition.rows, definition.cols),
      definition.grid,
    ).map((row) => row.replaceAll(CROSS, EMPTY)),
  };
}
